import { createReducer, createAction } from '@reduxjs/toolkit'
import { ExploreState, PoolData, TokenInfo, ScanProgress, NativePriceCache, V2PairData, V2ScanProgress, PoolTypeFilter } from './types'

// V3 Actions (existing)
export const addPools = createAction<{ chainId: number; pools: PoolData[] }>('explore/addPools')
export const setPools = createAction<{ chainId: number; pools: PoolData[] }>('explore/setPools')
export const updatePool = createAction<{ chainId: number; poolAddress: string; updates: Partial<PoolData> }>(
  'explore/updatePool'
)
export const addTokens = createAction<{ chainId: number; tokens: { [address: string]: TokenInfo } }>(
  'explore/addTokens'
)
export const setScanProgress = createAction<{ chainId: number; progress: Partial<ScanProgress> }>(
  'explore/setScanProgress'
)
export const setNativeCurrencyPrice = createAction<{ chainId: number; price: NativePriceCache }>(
  'explore/setNativeCurrencyPrice'
)
export const setHideSmallPools = createAction<boolean>('explore/setHideSmallPools')
export const setSmallPoolThresholdUsd = createAction<number>('explore/setSmallPoolThresholdUsd')
export const setSmallPoolThresholdNative = createAction<string>('explore/setSmallPoolThresholdNative')
export const clearChainData = createAction<number>('explore/clearChainData')
export const resetScan = createAction<number>('explore/resetScan')

// V2 Actions (new)
export const addV2Pairs = createAction<{ chainId: number; pairs: V2PairData[] }>('explore/addV2Pairs')
export const setV2Pairs = createAction<{ chainId: number; pairs: V2PairData[] }>('explore/setV2Pairs')
export const updateV2Pair = createAction<{ chainId: number; pairAddress: string; updates: Partial<V2PairData> }>(
  'explore/updateV2Pair'
)
export const setV2ScanProgress = createAction<{ chainId: number; progress: Partial<V2ScanProgress> }>(
  'explore/setV2ScanProgress'
)
export const clearV2ChainData = createAction<number>('explore/clearV2ChainData')
export const resetV2Scan = createAction<number>('explore/resetV2Scan')

// Filter action
export const setPoolTypeFilter = createAction<PoolTypeFilter>('explore/setPoolTypeFilter')

// Token Noxa Fun status update
export const updateTokenNoxaFunStatus = createAction<{ chainId: number; tokenAddress: string; isNoxaFunToken: boolean }>(
  'explore/updateTokenNoxaFunStatus'
)

// Initial state
const initialState: ExploreState = {
  pools: {},
  v2Pairs: {},
  tokens: {},
  scanProgress: {},
  v2ScanProgress: {},
  nativeCurrencyPrice: {},
  hideSmallPools: false,
  smallPoolThresholdUsd: 100,
  smallPoolThresholdNative: '0.001',
  poolTypeFilter: 'all',
}

// Reducer
export default createReducer(initialState, (builder) =>
  builder
    // V3 Pool actions
    .addCase(addPools, (state, action) => {
      const { chainId, pools } = action.payload
      if (!state.pools[chainId]) {
        state.pools[chainId] = []
      }
      const existingAddresses = new Set(
        state.pools[chainId]
          .filter((p) => p?.address)
          .map((p) => p.address.toLowerCase())
      )
      const newPools = pools.filter((p) => {
        if (!p?.address) return false
        const addr = p.address.toLowerCase()
        if (existingAddresses.has(addr)) return false
        existingAddresses.add(addr)
        return true
      })
      state.pools[chainId].push(...newPools)
    })
    .addCase(setPools, (state, action) => {
      const { chainId, pools } = action.payload
      state.pools[chainId] = pools
    })
    .addCase(updatePool, (state, action) => {
      const { chainId, poolAddress, updates } = action.payload
      const pools = state.pools[chainId]
      if (pools) {
        const poolIndex = pools.findIndex((p) => p.address.toLowerCase() === poolAddress.toLowerCase())
        if (poolIndex >= 0) {
          state.pools[chainId][poolIndex] = { ...pools[poolIndex], ...updates }
        }
      }
    })
    // V2 Pair actions
    .addCase(addV2Pairs, (state, action) => {
      const { chainId, pairs } = action.payload
      if (!state.v2Pairs[chainId]) {
        state.v2Pairs[chainId] = []
      }
      const existingAddresses = new Set(
        state.v2Pairs[chainId]
          .filter((p) => p?.address)
          .map((p) => p.address.toLowerCase())
      )
      const newPairs = pairs.filter((p) => {
        if (!p?.address) return false
        const addr = p.address.toLowerCase()
        if (existingAddresses.has(addr)) return false
        existingAddresses.add(addr)
        return true
      })
      state.v2Pairs[chainId].push(...newPairs)
    })
    .addCase(setV2Pairs, (state, action) => {
      const { chainId, pairs } = action.payload
      state.v2Pairs[chainId] = pairs
    })
    .addCase(updateV2Pair, (state, action) => {
      const { chainId, pairAddress, updates } = action.payload
      const pairs = state.v2Pairs[chainId]
      if (pairs) {
        const pairIndex = pairs.findIndex((p) => p.address.toLowerCase() === pairAddress.toLowerCase())
        if (pairIndex >= 0) {
          state.v2Pairs[chainId][pairIndex] = { ...pairs[pairIndex], ...updates }
        }
      }
    })
    // Token actions
    .addCase(addTokens, (state, action) => {
      const { chainId, tokens } = action.payload
      if (!state.tokens[chainId]) {
        state.tokens[chainId] = {}
      }
      for (const [address, token] of Object.entries(tokens)) {
        const lowerAddr = address.toLowerCase()
        // Preserve existing isNoxaFunToken if already set
        const existing = state.tokens[chainId][lowerAddr]
        state.tokens[chainId][lowerAddr] = {
          ...token,
          isNoxaFunToken: token.isNoxaFunToken ?? existing?.isNoxaFunToken,
        }
      }
    })
    .addCase(updateTokenNoxaFunStatus, (state, action) => {
      const { chainId, tokenAddress, isNoxaFunToken } = action.payload
      const lowerAddr = tokenAddress.toLowerCase()
      if (state.tokens[chainId]?.[lowerAddr]) {
        state.tokens[chainId][lowerAddr].isNoxaFunToken = isNoxaFunToken
      }
    })
    // V3 Scan progress
    .addCase(setScanProgress, (state, action) => {
      const { chainId, progress } = action.payload
      if (!state.scanProgress[chainId]) {
        state.scanProgress[chainId] = {
          lastScannedPositionId: '0',
          totalSupply: '0',
          isScanning: false,
          scanError: null,
        }
      }
      state.scanProgress[chainId] = { ...state.scanProgress[chainId], ...progress }
    })
    // V2 Scan progress
    .addCase(setV2ScanProgress, (state, action) => {
      const { chainId, progress } = action.payload
      if (!state.v2ScanProgress[chainId]) {
        state.v2ScanProgress[chainId] = {
          lastScannedPairIndex: '0',
          totalPairs: '0',
          isScanning: false,
          scanError: null,
        }
      }
      state.v2ScanProgress[chainId] = { ...state.v2ScanProgress[chainId], ...progress }
    })
    // Price
    .addCase(setNativeCurrencyPrice, (state, action) => {
      const { chainId, price } = action.payload
      state.nativeCurrencyPrice[chainId] = price
    })
    // User preferences
    .addCase(setHideSmallPools, (state, action) => {
      state.hideSmallPools = action.payload
    })
    .addCase(setSmallPoolThresholdUsd, (state, action) => {
      state.smallPoolThresholdUsd = action.payload
    })
    .addCase(setSmallPoolThresholdNative, (state, action) => {
      state.smallPoolThresholdNative = action.payload
    })
    .addCase(setPoolTypeFilter, (state, action) => {
      state.poolTypeFilter = action.payload
    })
    // Clear/reset actions
    .addCase(clearChainData, (state, action) => {
      const chainId = action.payload
      delete state.pools[chainId]
      delete state.v2Pairs[chainId]
      delete state.tokens[chainId]
      delete state.scanProgress[chainId]
      delete state.v2ScanProgress[chainId]
    })
    .addCase(resetScan, (state, action) => {
      const chainId = action.payload
      if (state.scanProgress[chainId]) {
        state.scanProgress[chainId] = {
          lastScannedPositionId: '0',
          totalSupply: '0',
          isScanning: false,
          scanError: null,
        }
      }
    })
    .addCase(clearV2ChainData, (state, action) => {
      const chainId = action.payload
      delete state.v2Pairs[chainId]
      delete state.v2ScanProgress[chainId]
    })
    .addCase(resetV2Scan, (state, action) => {
      const chainId = action.payload
      if (state.v2ScanProgress[chainId]) {
        state.v2ScanProgress[chainId] = {
          lastScannedPairIndex: '0',
          totalPairs: '0',
          isScanning: false,
          scanError: null,
        }
      }
    })
)
